/**
 * NPC → KayKit model mapping (pure, no three.js). Derived only from existing NpcDef fields
 * (tier, preferredRange, abilities, radius, colour), so content contracts stay untouched.
 * Edit the tables below to restyle enemies.
 */
import type { NpcDef, NpcTier } from '@keystone/sim';

export type MobModel = 'Skeleton_Minion' | 'Skeleton_Warrior' | 'Skeleton_Mage' | 'Knight' | 'Mage' | 'Barbarian' | 'Rogue_Hooded';
/** +field: procedural creatures (creatures.ts) for mobs without a KayKit model. */
export type ProcCreature = 'slime' | 'bloom' | 'boar' | 'wolf' | 'crawler' | 'wisp' | 'fox' | 'stag'; // +ranger-specs: fox, stag
export type MobWeapon = 'Skeleton_Blade' | 'Skeleton_Axe' | 'Skeleton_Staff' | 'Skeleton_Shield_Large_A';
export type Archetype = 'fodder' | 'bruiser' | 'caster' | 'big' | 'miniboss' | 'boss';

export interface MobVisual {
  archetype: Archetype;
  model: MobModel;
  /** [weapon asset, hand slot] pairs. */
  weapons: [MobWeapon, 'handslot.r' | 'handslot.l'][];
  /** Uniform model scale (KayKit skeletons stand ~2.2 yd at scale 1). */
  scale: number;
  /** Albedo tint (hex) multiplied into the model texture, and how strongly to apply it (0..1). */
  tint: string;
  tintAmount: number;
  /** Eye/rune glow colour. */
  glow: string;
  /** Clips (falling back to 'Idle' when missing). */
  idleClip: string;
  combatClip: string;
  castClip: string;
  /** +field: build a procedural creature instead of the KayKit model. */
  proc?: ProcCreature;
  /** +ranger-specs: a spirit creature (glows in its tint, with a halo: the Beastbound's beast). */
  spirit?: boolean;
  /** +field: gear nodes left visible inside a KayKit adventurer file (others are hidden). */
  gear?: string[];
}

/** +field: weapon / prop nodes inside the KayKit adventurer files (hidden unless listed in `gear`). */
export const ADVENTURER_GEAR = [
  '1H_Sword_Offhand', 'Badge_Shield', 'Rectangle_Shield', 'Round_Shield', 'Spike_Shield', '1H_Sword', '2H_Sword',
  'Spellbook', 'Spellbook_open', '1H_Wand', '2H_Staff', '1H_Axe_Offhand', 'Barbarian_Round_Shield', '1H_Axe', '2H_Axe', 'Mug',
  'Knife_Offhand', '1H_Crossbow', '2H_Crossbow', 'Knife', 'Throwable',
] as const;

/** One row per archetype. */
export const ARCHETYPE_VISUALS: Record<Archetype, Omit<MobVisual, 'archetype' | 'tint'>> = {
  fodder: { model: 'Skeleton_Minion', weapons: [['Skeleton_Blade', 'handslot.r']], scale: 0.85, tintAmount: 0.25, glow: '#66ff99', idleClip: 'Idle', combatClip: 'Idle_Combat', castClip: 'Spellcasting' },
  bruiser: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Blade', 'handslot.r']], scale: 1.0, tintAmount: 0.22, glow: '#66ff99', idleClip: 'Idle', combatClip: 'Idle_Combat', castClip: 'Spellcasting' },
  caster: { model: 'Skeleton_Mage', weapons: [['Skeleton_Staff', 'handslot.r']], scale: 1.0, tintAmount: 0.3, glow: '#66ff99', idleClip: 'Idle', combatClip: 'Spellcasting', castClip: 'Spellcasting' },
  big: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 1.45, tintAmount: 0.3, glow: '#7dffb0', idleClip: 'Idle', combatClip: 'Idle_Combat', castClip: 'Spellcasting' },
  miniboss: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 1.75, tintAmount: 0.38, glow: '#ffd36a', idleClip: 'Idle', combatClip: 'Idle_Combat', castClip: 'Spellcasting' },
  boss: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 2.3, tintAmount: 0.45, glow: '#ff9a5a', idleClip: 'Idle', combatClip: 'Idle_Combat', castClip: 'Spellcasting' },
};

/** Per-NPC overrides (by NpcDef id), applied on top of the archetype row. */
export const NPC_OVERRIDES: Record<string, Partial<Omit<MobVisual, 'archetype'>>> = {
  // Serpent-flavoured melee caster: keeps a staff but stays warrior-sized.
  spitting_serpent: { model: 'Skeleton_Minion', weapons: [['Skeleton_Staff', 'handslot.r']], scale: 1.05 },
  venom_priestess: { glow: '#5ef0e0' },
  ravenous_whelp: { scale: 0.7 },
  training_dummy: { weapons: [], scale: 1.3, tintAmount: 0 },
  // +tutorial (packages/content/src/dungeons/tutorial.ts)
  tutorial_dummy: { weapons: [], scale: 1.3, tintAmount: 0 },
  // +specs: Warden Ilsa's practice dummy (the Advancement Trial): a plain dummy, not a boss.
  tower_practice_dummy: { model: 'Skeleton_Minion', weapons: [], scale: 1.25, tintAmount: 0.15, tint: '#b8a27a', glow: '#ffd36a' },
  sparring_golem: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 1.5, tintAmount: 0.4, glow: '#ffb35a' },
  // +field: Mossvale (docs/design/zones.md). Sunmeadow critters are procedural creatures.
  meadow_slime: { proc: 'slime', tint: '#78d46a', scale: 1.0 },
  // +field: the treasure sprite: a small golden slime that runs away (it carries the loot).
  treasure_sprite: { proc: 'slime', tint: '#ffd23a', scale: 0.8 },
  bloom_slime: { proc: 'bloom', tint: '#9fdc8a', scale: 1.1 },
  tusk_boar: { proc: 'boar', tint: '#8c5e3c', scale: 1.0 },
  // Bramblewood: wolves and the Briar bandits (KayKit adventurers in bandit colours).
  bramble_wolf: { proc: 'wolf', tint: '#6f7a66', scale: 1.0 },
  briar_bandit: { model: 'Barbarian', weapons: [], gear: ['1H_Axe'], scale: 1.0, tint: '#b58a5e', tintAmount: 0.38, idleClip: 'Idle', combatClip: '2H_Melee_Idle', castClip: 'Spellcasting' },
  bandit_slinger: { model: 'Rogue_Hooded', weapons: [], gear: ['1H_Crossbow'], scale: 0.98, tint: '#8fa070', tintAmount: 0.4, idleClip: 'Idle', combatClip: '2H_Melee_Idle', castClip: 'Spellcasting' },
  bandit_captain: { model: 'Barbarian', weapons: [], gear: ['2H_Axe', 'Barbarian_Round_Shield'], scale: 1.25, tint: '#c4553f', tintAmount: 0.42, glow: '#ffd36a', idleClip: 'Idle', combatClip: '2H_Melee_Idle', castClip: 'Spellcasting' },
  // +zone: Glimmermere's crab-folk and croakers, Greyhorn's hounds and Tallow miners, the Pale Rise's sentinels and shades, and the rares.
  reedback_snapper: { proc: 'crawler', tint: '#d0643a', scale: 1.15 },
  mire_croaker: { proc: 'slime', tint: '#6f8f3a', scale: 1.15 },
  crag_hound: { proc: 'wolf', tint: '#8a929c', scale: 1.0 },
  tallow_digger: { model: 'Barbarian', weapons: [], gear: ['1H_Axe'], scale: 0.82, tint: '#a07a4a', tintAmount: 0.45, glow: '#ffcc66', idleClip: 'Idle', combatClip: '2H_Melee_Idle', castClip: 'Spellcasting' },
  tallow_chanter: { model: 'Mage', weapons: [], gear: ['1H_Wand'], scale: 0.82, tint: '#c09040', tintAmount: 0.45, glow: '#ffb84a', idleClip: 'Idle', combatClip: 'Spellcasting', castClip: 'Spellcasting' },
  pale_sentinel: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r']], scale: 1.2, tint: '#ece6d6', tintAmount: 0.55, glow: '#9fd8ff' },
  hollow_shade: { proc: 'wisp', tint: '#8fb0ff', scale: 1.15 },
  old_mossback: { proc: 'boar', tint: '#6f7a4a', scale: 1.6 },
  old_snapjaw: { proc: 'crawler', tint: '#e8783a', scale: 1.7 },
  foreman_tallowick: { model: 'Barbarian', weapons: [], gear: ['2H_Axe'], scale: 1.05, tint: '#d0a040', tintAmount: 0.5, glow: '#ffd36a', idleClip: 'Idle', combatClip: '2H_Melee_Idle', castClip: 'Spellcasting' },
  greymaw: { proc: 'wolf', tint: '#4f565c', scale: 1.5 },
  pale_warden: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 1.7, tint: '#f2eee2', tintAmount: 0.6, glow: '#7fd8ff' },
  // The Hollow Barrow: mossy dead, wisps and bone crawlers under the meadow.
  barrow_ghoul: { model: 'Skeleton_Minion', weapons: [['Skeleton_Blade', 'handslot.r']], scale: 0.9, tint: '#7fa27c', tintAmount: 0.45, glow: '#7dffd8' },
  barrow_wisp: { proc: 'wisp', tint: '#6ff0d8', scale: 1.0 },
  barrow_crawler: { proc: 'crawler', tint: '#b8b296', scale: 1.0 },
  barrow_bonecaller: { model: 'Skeleton_Mage', weapons: [['Skeleton_Staff', 'handslot.r']], scale: 1.5, tint: '#8fd8c8', tintAmount: 0.4, glow: '#5ef0e0' },
  // +acolyte-specs: the Heretic's Hollow One (a friendly pet): a gaunt dark horror with violet eyes.
  heretic_hollow_one: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Blade', 'handslot.r']], scale: 1.5, tint: '#2a1440', tintAmount: 0.7, glow: '#c98cff' },
  // +ranger-specs: the Beastbound's spirit beast: the wolf creature (and its fox / stag cousins), spectral.
  'beastbound.spirit_fox': { proc: 'fox', tint: '#ffb45a', scale: 1.0, spirit: true },
  'beastbound.spirit_wolf': { proc: 'wolf', tint: '#9fd0ff', scale: 1.05, spirit: true },
  'beastbound.great_stag': { proc: 'stag', tint: '#e8f5c8', scale: 1.0, spirit: true },
  barrow_king: { model: 'Skeleton_Warrior', weapons: [['Skeleton_Axe', 'handslot.r'], ['Skeleton_Shield_Large_A', 'handslot.l']], scale: 2.4, tint: '#e2c46e', tintAmount: 0.42, glow: '#7dffe0' },
};

export type NpcVisualInput = Pick<NpcDef, 'id' | 'tier' | 'color'> & Partial<Pick<NpcDef, 'preferredRange' | 'radius' | 'melee'>>;

export function archetypeOf(def: NpcVisualInput): Archetype {
  const t: NpcTier = def.tier;
  if (t === 'boss') return 'boss';
  if (t === 'miniboss') return 'miniboss';
  if (t === 'big') return 'big';
  if (t === 'fodder') return 'fodder';
  if (t === 'caster') {
    // Ranged casters keep distance; melee-range "casters" (frontal spitters) read as bruisers with a staff.
    return def.preferredRange !== undefined ? 'caster' : 'bruiser';
  }
  return 'bruiser';
}

export function npcVisual(def: NpcVisualInput): MobVisual {
  const archetype = archetypeOf(def);
  const row = ARCHETYPE_VISUALS[archetype];
  const base: MobVisual = { archetype, ...row, weapons: row.weapons.map((w) => [...w] as MobVisual['weapons'][number]), tint: def.color };
  const o = NPC_OVERRIDES[def.id];
  const v: MobVisual = o ? { ...base, ...o, weapons: o.weapons ? o.weapons.map((w) => [...w] as MobVisual['weapons'][number]) : base.weapons } : base;
  // Very wide collision radii (bosses) get a little extra size so the model fills its footprint.
  if (def.radius !== undefined && archetype === 'boss') v.scale = Math.max(v.scale, def.radius * 0.8);
  return v;
}

/** Fallback when only the tier is known (no NpcDef). */
export function npcVisualForTier(tier: NpcTier | null, color = '#c0473b'): MobVisual {
  return npcVisual({ id: '', tier: tier ?? 'standard', color, preferredRange: tier === 'caster' ? 30 : undefined });
}
